import React, { useEffect, useMemo, useState } from 'react';

import Grid from '../../../components/Grid/Grid';
import { makeEnding } from '../../../core/formatters';
import Button from '../../../components/Button/Button';
import { IconArrowRight, IconBack } from '../../../icons';
import { iPublication } from '../../../core/models/Publication';
import { ContentRibbon } from '../../../core/models/ContentRibbon';
import { getSortTypes, SORT_ENUM, SortTypesEnum } from '../../../components/Filters/Filters.helpers';
import Select from '../../../components/Select/Select';
import SearchResultsSectionSkeleton from './SearchResultsSectionSkeleton';

interface iOtherResultsProps {
    slideTilesCount: number;
    otherResultsCount: number;
    otherResults: iPublication[];
    isAiSearchEnabled: boolean;
    otherResultsStartIndex: number;
    isLoading: boolean;
}

export const sortOptions = [
    { id: SortTypesEnum.MostRelevant, title: 'Most relevant' },
    { id: SortTypesEnum.DateDesc, title: 'Newest first' },
    { id: SortTypesEnum.DateAsc, title: 'Oldest first' },
    { id: SortTypesEnum.TitleAsc, title: 'Publication name A → Z' },
    { id: SortTypesEnum.TitleDesc, title: 'Publication name Z → A' }
];

const OtherResults = ({
    slideTilesCount,
    otherResultsCount,
    otherResults,
    isAiSearchEnabled,
    otherResultsStartIndex,
    isLoading
}: iOtherResultsProps) => {
    const [isOtherResultsOpened, setIsOtherResultsOpened] = useState(false);
    const [selectedSortType, setSelectedSortType] = useState<SortTypesEnum[]>([SortTypesEnum.MostRelevant]);

    useEffect(() => {
        setIsOtherResultsOpened(false);
    }, [isLoading]);

    const preSlicedResults = useMemo(() => otherResults.slice(otherResultsStartIndex), [otherResults, otherResultsStartIndex]);

    const sortedResults = useMemo(() => {
        const sortType = selectedSortType[0] || SortTypesEnum.MostRelevant;
        const { sortBy, direction } = getSortTypes(sortType);

        if (sortType === SortTypesEnum.MostRelevant) {
            return preSlicedResults;
        }

        return [...preSlicedResults].sort((a, b) => {
            let aValue: string | number = '';
            let bValue: string | number = '';

            if (sortBy === SORT_ENUM.DATE) {
                aValue = new Date(a.createdDate).getTime();
                bValue = new Date(b.createdDate).getTime();
            } else if (sortBy === SORT_ENUM.TITLE) {
                aValue = a.title.toLowerCase();
                bValue = b.title.toLowerCase();
            }
            if (aValue < bValue) return direction === SORT_ENUM.ASC ? -1 : 1;
            if (aValue > bValue) return direction === SORT_ENUM.ASC ? 1 : -1;
            return 0;
        });
    }, [preSlicedResults, selectedSortType]);

    const otherResultsToRender = useMemo(() => {
        if (!isOtherResultsOpened) {
            return sortedResults.slice(0, slideTilesCount);
        }

        return sortedResults;
    }, [isOtherResultsOpened, sortedResults, slideTilesCount]);

    if (isLoading) {
        return <SearchResultsSectionSkeleton title="Suggested Titles" tilesCount={slideTilesCount} />;
    }

    if (otherResults.length === 0) {
        return null;
    }

    return (
        <div className={`GlobalSearch__result ${isOtherResultsOpened ? 'collapsed' : ''}`}>
            <Grid
                columnsNumber={slideTilesCount}
                data={
                    new ContentRibbon({
                        clientModel: {
                            id: 3,
                            title: (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        width: '100%'
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        Suggested Titles
                                        <Button
                                            className="ShowAllButton"
                                            onClick={() => setIsOtherResultsOpened(!isOtherResultsOpened)}
                                        >
                                            {isOtherResultsOpened && <IconBack />}
                                            Show {isOtherResultsOpened ? 'less' : 'all'}
                                            {!isOtherResultsOpened && <IconArrowRight />}
                                        </Button>
                                    </div>
                                    <div className="GlobalSearch__select-sorting">
                                        <Select
                                            title="Sort by:"
                                            options={sortOptions}
                                            value={selectedSortType}
                                            isLoading={false}
                                            onChange={(ids) => {
                                                const sortType = ids[0] as SortTypesEnum;
                                                if (sortType) {
                                                    setSelectedSortType([sortType]);
                                                }
                                            }}
                                            isCheckboxHidden
                                            isSimpleInput
                                            dropdownPosition="right"
                                            dropdownHeight={140}
                                        />
                                    </div>
                                </div>
                            ),
                            subTitle: `found in ${
                                isAiSearchEnabled && otherResultsCount > 200 ? '200+' : otherResultsCount
                            } ${makeEnding('report', otherResultsCount)}`,
                            items: otherResultsToRender
                        }
                    })
                }
            />
        </div>
    );
};

export default OtherResults;
